import Chip from "@mui/material/Chip"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import { approvalModeLabel, formatMaxAmount } from "../../constants"

// Vertical numbered chain — shared visual language for Preview/Test and step config.
const ResolvedChainPreview = ({ steps = [], compact = false }) => {
  if (!steps.length) {
    return (
      <MDTypography variant="body2" color="text">
        No approval steps to preview.
      </MDTypography>
    )
  }

  return (
    <MDBox>
      {steps.map((s, idx) => {
        const isLast = idx === steps.length - 1
        const order = s.stepOrder ?? s.order ?? idx + 1
        const approverNames = (s.approvers || [])
          .map((a) => a.username || a.email)
          .filter(Boolean)
          .join(", ")
        const label =
          approverNames || s.approverLabel || s.label || s.approverRef || s.approver
        const mode = s.mode || "sequential"
        const quorum = s.quorum ?? 1

        // Email steps carry a raw comma-separated address string as their
        // label — render as wrapping chips instead of one run-on line that
        // pushes past the card. Falls back to sniffing "@" so a backend
        // preview response (no approverType) still gets the same treatment.
        const isEmails =
          s.approverType === "emails" || (typeof label === "string" && label.includes("@"))
        const emailChips = isEmails
          ? label
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : []

        return (
          <MDBox key={`${order}-${label}`} display="flex" gap={1.5}>
            <MDBox display="flex" flexDirection="column" alignItems="center" width="2rem">
              <MDBox
                width={compact ? "1.75rem" : "2rem"}
                height={compact ? "1.75rem" : "2rem"}
                borderRadius="50%"
                bgColor="info"
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <MDTypography variant="caption" color="white" fontWeight="bold">
                  {order}
                </MDTypography>
              </MDBox>
              {!isLast && (
                <MDBox width="2px" flex={1} minHeight="1.5rem" bgColor="light" my={0.5} />
              )}
            </MDBox>
            <MDBox flex={1} pb={isLast ? 0 : 2} sx={{ minWidth: 0 }}>
              <MDBox p={compact ? 1.5 : 2} borderRadius="lg" bgColor="grey-100">
                {emailChips.length > 0 ? (
                  <MDBox display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                    {emailChips.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        size="small"
                        sx={{
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                    ))}
                  </MDBox>
                ) : (
                  <MDTypography variant="button" fontWeight="medium" display="block">
                    {label}
                  </MDTypography>
                )}
                <MDTypography variant="caption" color="text">
                  {approvalModeLabel(mode)} · needs {quorum} approvers
                  {formatMaxAmount(s.maxAmount) ? ` · ${formatMaxAmount(s.maxAmount)}` : ""}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        )
      })}
    </MDBox>
  )
}

export default ResolvedChainPreview
